/**
 * Toasts. Los errores del contrato §0 llegan con `rule` (`RN-PLA-24`): la regla viaja en
 * el `data-rule` de la tarjeta para trazabilidad y tests, pero **no se dibuja** — el
 * cliente ve el mensaje en castellano, no un código (CLAUDE.md, sección Frontend).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from "lucide-react";
import { cn } from "../../lib/cn.js";
import { ApiError } from "../../lib/api.js";

export type ToastTone = "success" | "error" | "warning" | "info";

export interface ToastInput {
  tone?: ToastTone;
  title: string;
  description?: ReactNode;
  /** Regla del AF que explica el rechazo (`error.rule`). Trazabilidad: no se muestra. */
  rule?: string;
  durationMs?: number;
}

interface ToastItem extends ToastInput {
  id: number;
  tone: ToastTone;
}

interface ToastApi {
  push: (toast: ToastInput) => void;
  success: (title: string, description?: ReactNode) => void;
  error: (title: string, description?: ReactNode) => void;
  /** Atajo para un `ApiError`: usa su mensaje y su `rule`. */
  fromError: (error: unknown, fallbackTitle?: string) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const ICONS: Record<ToastTone, typeof Info> = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: TriangleAlert,
  info: Info,
};

const TONE_CLASS: Record<ToastTone, string> = {
  success: "border-success/40",
  error: "border-danger/45",
  warning: "border-warning/40",
  info: "border-line",
};

const ICON_CLASS: Record<ToastTone, string> = {
  success: "text-success-fg",
  error: "text-danger",
  warning: "text-warning",
  info: "text-accent-live",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const push = useCallback((toast: ToastInput) => {
    const id = nextId.current++;
    setItems((current) => [...current, { ...toast, id, tone: toast.tone ?? "info" }]);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      push,
      dismiss,
      success: (title, description) => push({ tone: "success", title, description }),
      error: (title, description) => push({ tone: "error", title, description }),
      fromError: (error, fallbackTitle = "No se pudo completar la acción") => {
        if (error instanceof ApiError) {
          push({
            tone: error.code === "FORBIDDEN" ? "warning" : "error",
            title: error.message,
            ...(error.rule ? { rule: error.rule } : {}),
          });
          return;
        }
        push({
          tone: "error",
          title: fallbackTitle,
          description: error instanceof Error ? error.message : undefined,
        });
      },
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-[min(380px,calc(100vw-2rem))] flex-col gap-2"
      >
        {items.map((item) => (
          <ToastCard key={item.id} item={item} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  const Icon = ICONS[item.tone];

  useEffect(() => {
    const timeout = window.setTimeout(() => onDismiss(item.id), item.durationMs ?? 6000);
    return () => window.clearTimeout(timeout);
  }, [item.id, item.durationMs, onDismiss]);

  return (
    <div
      role="status"
      data-rule={item.rule}
      className={cn(
        "panel-glass pointer-events-auto flex items-start gap-2.5 px-3.5 py-3 animate-[viva-in_.16s_ease-out]",
        TONE_CLASS[item.tone],
      )}
    >
      <Icon aria-hidden className={cn("mt-0.5 size-4 shrink-0", ICON_CLASS[item.tone])} />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium text-ink">{item.title}</p>
        {item.description ? (
          <div className="mt-0.5 text-[12px] leading-snug text-dim">{item.description}</div>
        ) : null}
      </div>
      <button
        type="button"
        aria-label="Cerrar aviso"
        onClick={() => onDismiss(item.id)}
        className="-mr-1 -mt-1 rounded-[6px] p-1 text-mute transition-colors hover:text-ink"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast necesita un <ToastProvider> arriba.");
  return context;
}
